import { Button, Stack } from '@rossum/ui/material';
import {
  DateRange,
  DateRangeCalendar,
  dateRangeCalendarClasses,
  dateRangePickerDayClasses,
  LocalizationProvider,
} from '@rossum/ui/x-date-pickers-pro';
import { AdapterDateFns } from '@rossum/ui/x-date-pickers-pro/AdapterDateFns';
import { isValid } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { dateFnsLocales } from '../../../../i18n/dateLocales';
import { isRangedDateOperator, SelectedDates } from '../../mql/dateUtils';
import { DateOperators } from '../../mql/operators';
import { DATE_RANGE_CALENDAR_WIDTH, DateTimeInput } from './DateTimeInput';
import { handleNonRangedDateChange } from './handleNonRangedDateChange';
import {
  getCustomDateRangeTime,
  getFixedDateShortcuts,
  getIsActive,
  rangedDateShortcuts,
} from './shortcuts';
import { applyTextButtonStyle, shortcutStyles } from './styles';

const { formatTokenMap } = new AdapterDateFns();

type DateTimePickerProps = {
  selectedDates: SelectedDates;
  setSelectedDates: (dateRange: SelectedDates) => void;
  operatorValue: DateOperators[number]['value'];
  hideTimePickers?: boolean;
};

export const DateTimeRangePicker = ({
  selectedDates,
  setSelectedDates,
  operatorValue,
  hideTimePickers,
}: DateTimePickerProps) => {
  const intl = useIntl();
  const locale = dateFnsLocales[intl.locale] ?? dateFnsLocales.en;

  const isRangedOperator = isRangedDateOperator(operatorValue);

  const handleDatePickerChange = (
    dateRange: DateRange<Date>,
    selectionState?: 'partial' | 'finish' | 'shallow'
  ) => {
    const [from, to] = dateRange;

    if (!isRangedOperator) {
      const targetDate = handleNonRangedDateChange({
        dateRange,
        selectionState,
        selectedDates,
        operatorValue,
      });
      return setSelectedDates([targetDate, targetDate]);
    }

    if (!from || !to) return setSelectedDates(dateRange);

    return setSelectedDates(
      getCustomDateRangeTime([from, to], isRangedOperator)
    );
  };

  const shortcuts = useMemo(() => {
    const fixedShortcuts = getFixedDateShortcuts(operatorValue);

    return [...fixedShortcuts, ...rangedDateShortcuts];
  }, [operatorValue]);

  return (
    <Stack direction="row">
      <Stack
        my={1}
        pr={4}
        minWidth={150}
        sx={{
          borderRight: theme => `1px solid ${theme.palette.other.muted}`,
        }}
      >
        {shortcuts.map(shortcut => (
          <Button
            key={shortcut.labelKey}
            onClick={() => {
              setSelectedDates(shortcut.getValue());
            }}
            variant="text"
            sx={{
              ...shortcutStyles,
              ...applyTextButtonStyle(
                getIsActive(selectedDates, shortcut.getValue())
              ),
            }}
          >
            {intl.formatMessage({
              id: `containers.filtering.shortcuts.${shortcut.labelKey}`,
            })}
          </Button>
        ))}
      </Stack>
      <Stack>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={locale}
        >
          <DateRangeCalendar
            value={selectedDates}
            onChange={handleDatePickerChange}
            sx={{
              [`& .${dateRangePickerDayClasses.rangeIntervalPreview}`]:
                isRangedOperator
                  ? {}
                  : {
                      border: 'none',
                      // to account for border-width
                      margin: '2px',
                    },
              [`& .${dateRangeCalendarClasses.monthContainer}:not(:last-of-type)`]:
                {
                  borderRight: 'none',
                },
            }}
          />

          <Stack direction="row" maxWidth={DATE_RANGE_CALENDAR_WIDTH}>
            <DateTimeInput
              date-cy="date-time-range-picker-start"
              value={selectedDates[0]}
              isActive={!selectedDates[0]}
              onChange={value => {
                if (value && isValid(value)) {
                  setSelectedDates([value, selectedDates[1]]);
                }
              }}
              formatTokenMap={formatTokenMap}
              hideTime={hideTimePickers}
            />

            {isRangedOperator ? (
              <DateTimeInput
                data-cy="date-time-range-picker-end"
                value={selectedDates[1]}
                isActive={
                  !!(isRangedOperator && selectedDates[0] && !selectedDates[1])
                }
                onChange={value => {
                  if (value && isValid(value)) {
                    setSelectedDates([selectedDates[0], value]);
                  }
                }}
                formatTokenMap={formatTokenMap}
                hideTime={hideTimePickers}
              />
            ) : null}
          </Stack>
        </LocalizationProvider>
      </Stack>
    </Stack>
  );
};
